import React, { useState } from "react";
import { format, parse, addDays } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Clock, Plus, Minus, Calendar } from "lucide-react";
import { toast } from "@/hooks/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { EmployeeShiftDisplayData } from "@/types/time-tracking";
import { useAuth } from "@/context/AuthContext";

interface ManualTimeEntryDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEmployeeShift: EmployeeShiftDisplayData | null;
  selectedDate: Date;
  businessId: string;
  eventType: 'arrival' | 'departure';
  onSave: () => void;
}

const ManualTimeEntryDialog: React.FC<ManualTimeEntryDialogProps> = ({
  isOpen,
  onOpenChange,
  selectedEmployeeShift,
  selectedDate,
  businessId,
  eventType,
  onSave
}) => {
  const [hours, setHours] = useState<number>(9);
  const [minutes, setMinutes] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { user } = useAuth();

  React.useEffect(() => {
    if (selectedEmployeeShift && isOpen) {
      const existingTime = eventType === 'arrival'
        ? selectedEmployeeShift.actualArrivalTime
        : selectedEmployeeShift.actualDepartureTime;

      if (existingTime) {
        console.log('Parsing existing time:', existingTime, 'for', eventType);
        try {
          // Try parsing as localized time (e.g., "9:30 AM")
          let parsedTime = parse(existingTime, 'p', new Date());
          console.log('After parse with p format:', parsedTime, 'isValid:', !isNaN(parsedTime.getTime()));

          // If that fails, try HH:mm format
          if (isNaN(parsedTime.getTime())) {
            parsedTime = parse(existingTime, 'HH:mm', new Date());
            console.log('After parse with HH:mm format:', parsedTime, 'isValid:', !isNaN(parsedTime.getTime()));
          }

          // If still failing, try h:mm a format
          if (isNaN(parsedTime.getTime())) {
            parsedTime = parse(existingTime, 'h:mm a', new Date());
            console.log('After parse with h:mm a format:', parsedTime, 'isValid:', !isNaN(parsedTime.getTime()));
          }

          const hour24 = parsedTime.getHours();
          const minute = parsedTime.getMinutes();
          console.log('Parsed hours:', hour24, 'minutes:', minute);

          // Only set if we got valid numbers
          if (!isNaN(hour24) && !isNaN(minute)) {
            setHours(hour24);
            setMinutes(minute);
            console.log('Set hours to', hour24, 'and minutes to', minute);
          } else {
            // Fallback to current time
            console.warn('Failed to parse time, using current time');
            const now = new Date();
            setHours(now.getHours());
            setMinutes(Math.floor(now.getMinutes() / 5) * 5);
          }
        } catch (error) {
          console.error('Error parsing time:', existingTime, error);
          // Fallback to current time
          const now = new Date();
          setHours(now.getHours());
          setMinutes(Math.floor(now.getMinutes() / 5) * 5);
        }
      } else {
        // Default to current time
        const now = new Date();
        const hour24 = now.getHours();
        const minute = now.getMinutes();

        setHours(hour24);
        setMinutes(Math.floor(minute / 5) * 5); // Round to nearest 5 minutes
      }
    }
  }, [selectedEmployeeShift, eventType, isOpen]);

  // Helper functions for time manipulation
  const incrementHours = () => {
    setHours(prev => {
      const current = isNaN(prev) ? 9 : prev;
      return (current + 1) % 24;
    });
  };

  const decrementHours = () => {
    setHours(prev => {
      const current = isNaN(prev) ? 9 : prev;
      return current === 0 ? 23 : current - 1;
    });
  };

  const incrementMinutes = () => {
    setMinutes(prev => {
      const current = isNaN(prev) ? 0 : prev;
      return (current + 5) % 60;
    });
  };

  const decrementMinutes = () => {
    setMinutes(prev => {
      const current = isNaN(prev) ? 0 : prev;
      return current === 0 ? 55 : current - 5;
    });
  };

  const setToNow = () => {
    const now = new Date();
    const hour24 = now.getHours();
    const minute = now.getMinutes();

    setHours(hour24);
    setMinutes(Math.floor(minute / 5) * 5);
  };

  const setToShiftTime = (type: 'start' | 'end') => {
    if (!selectedEmployeeShift) return;

    const timeStr = type === 'start'
      ? selectedEmployeeShift.scheduledStartTime
      : selectedEmployeeShift.scheduledEndTime;

    const parsedTime = parse(timeStr, 'HH:mm', new Date());
    const hour24 = parsedTime.getHours();
    const minute = parsedTime.getMinutes();

    setHours(hour24);
    setMinutes(minute);
  };

  const handleSaveManualTime = async () => {
    if (!selectedEmployeeShift || !businessId || !selectedEmployeeShift.employeeId) {
      toast.error(`Missing employee or business data for manual ${eventType}.`);
      return;
    }

    // Use safe values to prevent NaN from being saved
    const safeHours = isNaN(hours) ? 9 : hours;
    const safeMinutes = isNaN(minutes) ? 0 : minutes;

    setIsSaving(true);
    try {
      // Combine selectedDate with time
      const timeDateTime = new Date(selectedDate);
      timeDateTime.setHours(safeHours);
      timeDateTime.setMinutes(safeMinutes);
      timeDateTime.setSeconds(0);
      timeDateTime.setMilliseconds(0);

      const scannedAtISO = timeDateTime.toISOString();

      const startOfSelectedDay = new Date(selectedDate);
      startOfSelectedDay.setHours(0, 0, 0, 0);

      // Check if an existing log exists for this employee on this day
      const { data: existingLog, error: fetchError } = await supabase
        .from('time_logs')
        .select('*')
        .eq('employee_id', selectedEmployeeShift.employeeId)
        .eq('business_id', businessId)
        .eq('event_type', eventType)
        .gte('scanned_at', startOfSelectedDay.toISOString())
        .lt('scanned_at', addDays(startOfSelectedDay, 1).toISOString())
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 means no rows found
        throw fetchError;
      }

      if (existingLog) {
        // Update existing log
        const { error: updateError } = await supabase
          .from('time_logs')
          .update({ scanned_at: scannedAtISO })
          .eq('id', existingLog.id);

        if (updateError) {
          throw updateError;
        }

        // Log the manual edit activity
        await supabase.from('time_tracking_activity_log').insert({
          business_id: businessId,
          employee_id: selectedEmployeeShift.employeeId,
          event_type: 'manual_edit',
          event_timestamp: new Date().toISOString(),
          time_log_id: existingLog.id,
          actor_user_id: user?.id || null,
          metadata: {
            previous_time: existingLog.scanned_at,
            new_time: scannedAtISO,
            field: eventType,
            auth_method: 'manual',
          },
        });

        toast.success(`${selectedEmployeeShift.employeeName}'s ${eventType} time updated to ${format(timeDateTime, "p")}.`);
      } else {
        // Insert new log
        const { error: insertError } = await supabase
          .from('time_logs')
          .insert({
            employee_id: selectedEmployeeShift.employeeId,
            business_id: businessId,
            event_type: eventType,
            scanned_at: scannedAtISO,
          });

        if (insertError) {
          throw insertError;
        }
        toast.success(`${selectedEmployeeShift.employeeName}'s ${eventType} time recorded as ${format(timeDateTime, "p")}.`);
      }

      onOpenChange(false);
      onSave();
    } catch (err: any) {
      toast.error(`Failed to save manual ${eventType} time.`, { description: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedEmployeeShift || !businessId || !selectedEmployeeShift.employeeId) {
      toast.error(`Missing employee or business data for reset.`);
      return;
    }
    
    setIsSaving(true);
    try {
      const startOfSelectedDayForReset = new Date(selectedDate);
      startOfSelectedDayForReset.setHours(0, 0, 0, 0);

      // First get the log we're about to delete for activity logging
      const { data: logToDelete } = await supabase
        .from('time_logs')
        .select('*')
        .eq('employee_id', selectedEmployeeShift.employeeId)
        .eq('business_id', businessId)
        .eq('event_type', eventType)
        .gte('scanned_at', startOfSelectedDayForReset.toISOString())
        .lt('scanned_at', addDays(startOfSelectedDayForReset, 1).toISOString())
        .single();

      const { error: deleteError } = await supabase
        .from('time_logs')
        .delete()
        .eq('employee_id', selectedEmployeeShift.employeeId)
        .eq('business_id', businessId)
        .eq('event_type', eventType)
        .gte('scanned_at', startOfSelectedDayForReset.toISOString())
        .lt('scanned_at', addDays(startOfSelectedDayForReset, 1).toISOString());

      if (deleteError) {
        throw deleteError;
      }

      // Log the manual delete activity
      if (logToDelete) {
        await supabase.from('time_tracking_activity_log').insert({
          business_id: businessId,
          employee_id: selectedEmployeeShift.employeeId,
          event_type: 'manual_delete',
          event_timestamp: new Date().toISOString(),
          actor_user_id: user?.id || null,
          metadata: {
            previous_time: logToDelete.scanned_at,
            field: eventType,
            auth_method: 'manual',
          },
        });
      }

      toast.success(`${selectedEmployeeShift.employeeName}'s ${eventType} time has been reset.`);
      onOpenChange(false);
      onSave();
    } catch (err: any) {
      toast.error(`Failed to reset manual ${eventType} time.`, { description: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const hasExistingTime = eventType === 'arrival' 
    ? selectedEmployeeShift?.actualArrivalTime 
    : selectedEmployeeShift?.actualDepartureTime;

  // Format display time - ensure valid numbers
  const safeHours = isNaN(hours) ? 9 : hours;
  const safeMinutes = isNaN(minutes) ? 0 : minutes;
  const displayTime = `${safeHours.toString().padStart(2, '0')}:${safeMinutes.toString().padStart(2, '0')}`;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Set {eventType === 'arrival' ? 'Arrival' : 'Departure'} Time
          </DialogTitle>
          <DialogDescription>
            {selectedEmployeeShift?.employeeName} • {format(selectedDate, "PPP")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Large Time Display */}
          <div className="text-center">
            <div className="text-6xl font-bold tabular-nums tracking-tight text-primary">
              {displayTime}
            </div>
          </div>

          {/* Time Spinners */}
          <div className="flex items-center justify-center gap-6">
            {/* Hours Spinner */}
            <div className="flex flex-col items-center gap-2">
              <Label className="text-xs text-muted-foreground">Hours</Label>
              <div className="flex flex-col items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-20"
                  onClick={incrementHours}
                  type="button"
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <div className="h-14 w-20 flex items-center justify-center bg-muted rounded-md text-3xl font-bold tabular-nums">
                  {safeHours.toString().padStart(2, '0')}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-20"
                  onClick={decrementHours}
                  type="button"
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="text-4xl font-bold text-muted-foreground self-center mt-6">:</div>

            {/* Minutes Spinner */}
            <div className="flex flex-col items-center gap-2">
              <Label className="text-xs text-muted-foreground">Minutes</Label>
              <div className="flex flex-col items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-20"
                  onClick={incrementMinutes}
                  type="button"
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <div className="h-14 w-20 flex items-center justify-center bg-muted rounded-md text-3xl font-bold tabular-nums">
                  {safeMinutes.toString().padStart(2, '0')}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-20"
                  onClick={decrementMinutes}
                  type="button"
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Time Presets */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Quick Select</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={setToNow}
                type="button"
                className="gap-1"
              >
                <Clock className="h-3 w-3" />
                Now
              </Button>
              {selectedEmployeeShift && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setToShiftTime('start')}
                    type="button"
                    className="gap-1"
                  >
                    <Calendar className="h-3 w-3" />
                    Shift Start ({selectedEmployeeShift.scheduledStartTime})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setToShiftTime('end')}
                    type="button"
                    className="gap-1"
                  >
                    <Calendar className="h-3 w-3" />
                    Shift End ({selectedEmployeeShift.scheduledEndTime})
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {hasExistingTime && (
            <Button
              variant="destructive"
              onClick={handleReset}
              disabled={isSaving}
            >
              Clear Time
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveManualTime} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Time"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualTimeEntryDialog;